import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import { getMyHistory, type Assessment } from '../api/assessment';
import { useAuth } from '../store/AuthContext';
import { exportToCsv, todayIso } from '../utils/exportCsv';

const dimensionOrder = ['d1', 'd2', 'd3', 'd4', 'd5', 'd6'];
const dimensionLabel: Record<string, string> = {
  d1: 'Tooling',
  d2: 'Prompts',
  d3: 'Workflow',
  d4: 'Skill',
  d5: 'Measurement',
  d6: 'Culture',
};

export function DevDashboard() {
  const { user } = useAuth();
  const [history, setHistory] = useState<Assessment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMyHistory()
      .then((d) => setHistory(d.assessments))
      .catch((err: unknown) => {
        const m = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setError(m ?? 'Failed to load history');
      });
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" component="h1">
            Welcome{user ? `, ${user.email}` : ''}
          </Typography>
          <Typography variant="body2">
            {user?.role === 'exec' ? 'Self-evaluation account' : 'Track your AI maturity over time'}
          </Typography>
        </Box>
        <Button component={RouterLink} to="/assessment" variant="contained" color="primary">
          TAKE NEW ASSESSMENT
        </Button>
      </Box>

      {error && <Alert severity="error" role="alert">{error}</Alert>}

      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h5" component="h2">
              My History
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={!history || history.length === 0}
              onClick={() => {
                if (!history || history.length === 0) return;
                const rows = history.map((a) => ({
                  period: a.cadencePeriod,
                  submitted: new Date(a.submittedAt).toISOString(),
                  totalScore: a.totalScore.toFixed(2),
                  maturityLevel: a.maturityLevel,
                  d1: (a.dimensionScores?.d1 ?? 0).toFixed(2),
                  d2: (a.dimensionScores?.d2 ?? 0).toFixed(2),
                  d3: (a.dimensionScores?.d3 ?? 0).toFixed(2),
                  d4: (a.dimensionScores?.d4 ?? 0).toFixed(2),
                  d5: (a.dimensionScores?.d5 ?? 0).toFixed(2),
                  d6: (a.dimensionScores?.d6 ?? 0).toFixed(2),
                }));
                exportToCsv(`ai-maturity-my-history-${user?.email ?? 'me'}-${todayIso()}.csv`, rows);
              }}
            >
              {!history || history.length === 0 ? 'No data' : 'Export CSV'}
            </Button>
          </Box>
          {!history && !error ? (
            <CircularProgress />
          ) : history && history.length === 0 ? (
            <Typography variant="body2">
              You haven't completed an assessment yet. Click <strong>Take New Assessment</strong> to begin.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell align="right">Total Score</TableCell>
                    <TableCell align="right">Level</TableCell>
                    {dimensionOrder.map((d) => (
                      <TableCell key={d} align="right">{dimensionLabel[d]}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(history ?? []).map((a) => (
                    <TableRow key={a.assessmentId}>
                      <TableCell>{a.cadencePeriod}</TableCell>
                      <TableCell>{new Date(a.submittedAt).toLocaleString()}</TableCell>
                      <TableCell align="right">{a.totalScore.toFixed(2)}</TableCell>
                      <TableCell align="right">L{a.maturityLevel}</TableCell>
                      {dimensionOrder.map((d) => (
                        <TableCell key={d} align="right">
                          {(a.dimensionScores?.[d] ?? 0).toFixed(2)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
